import { useCallback, useEffect, useRef, useState } from 'react';
import { API_BASE, AUTH_TOKEN, IS_GUEST } from '../config';
import { refreshMyRecords } from '../services/myRecords';
import { useWorldStore } from '../state/worldStore';

// useDialogueScene — one directed conversation, wherever it is being painted.
//
// Extracted from SceneDock the moment a SECOND surface needed it (the phone's
// call screen). A directed scene carries real consequences — it spends the
// player's generation budget and writes to their profile — and the rules that
// make that safe (carry `pending` every turn so the scene can't be closed
// mid-render; sync the quest only after the server says a field was recorded)
// are not rules that survive being reimplemented per surface. One engine, many
// screens: the phone and the room disagree about how a line LOOKS and about
// nothing else.
//
// Everything authored lives server-side in the dialogue director. This holds
// the transcript and the two pieces of client state the server can't see: which
// render we're watching, and whether we've opened this scene yet.
// What a player sees when a turn fails. Server errors are written for us, not
// for them — "Unknown scene: act5_handoff" on a phone screen is a crash report.
function humanError(err) {
  const m = String(err?.message || '');
  if (/unknown scene/i.test(m)) return "Line's dead — they'll call back.";
  if (/dialogue 5\d\d|failed to fetch|network/i.test(m)) return 'Signal dropped — try that again.';
  return m;
}

export function useDialogueScene({ sceneKey, quest = null, avatarId }) {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [pending, setPending] = useState(null);   // { running, label } while a track renders
  const [closed, setClosed] = useState(false);
  const [note, setNote] = useState('');
  const [track, setTrack] = useState(null);       // { trackId, title } once started
  const [opening, setOpening] = useState(false);  // fetching the first line
  // Tappable replies for the CURRENT question — same {label, value} contract the
  // website's chat uses. Cleared the moment one is sent: the transcript is not
  // persisted here, so the tapped answer simply becomes the player's own line
  // rather than needing the website's chosen/disabled bookkeeping.
  const [choices, setChoices] = useState([]);
  // The wallet signal — { required, balance, short } while a charging scene
  // can't be covered; the surface renders the top-up panel from it. Refreshed
  // every turn, so a successful take_payment clears it.
  const [wallet, setWallet] = useState(null);
  // What the last turn RECORDED ([{ field, value }]) — surfaces react to
  // writes (the salon fires the look render when `appearance` lands).
  const [lastRecorded, setLastRecorded] = useState([]);
  // WORLD NOTES — parenthetical lines the WORLD sends into the scene ("(The
  // car is passing the Concert Hall — …)"). They travel as user-role messages
  // because the transcript only has two sides, but they are flagged `world`
  // so surfaces paint them as ambience, never as the player's own bubble. The
  // scene's direction teaches the NPC the convention: a parenthesis is what's
  // happening outside, not something the player said.
  const [worldNotes, setWorldNotes] = useState([]);
  // The NPC's own avatar, when it HAS one. Most scene characters are written
  // rather than cast (Rae, Ro and Book don't exist as avatars, so they have no
  // voice to be spoken in) — the server sends this only when the NPC maps to a
  // real avatar with a voice configured, so "silent" is the safe default and no
  // TTS request is ever fired that could only fail.
  const [voiceAvatarId, setVoiceAvatarId] = useState(null);

  const pendingRef = useRef(null);   // read by turn() — state would be a turn stale
  const openedRef = useRef(null);    // which scene key we've already opened
  const startedRef = useRef(null);   // the track this scene cut; survives completion
  const pollRef = useRef(null);

  const setPendingBoth = (v) => { pendingRef.current = v; setPending(v); };

  const turn = useCallback(async (nextMessages, opts = {}) => {
    // THE GUEST DOOR (Khari, 2026-08-02): no account yet → the unauthenticated
    // endpoint, no avatar, and the answers-so-far ride along as guestFields —
    // the server holds nothing for a stranger; the client is the state.
    const guest = IS_GUEST || !avatarId;
    const url = guest ? `${API_BASE}/api/dialogue/guest/turn` : `${API_BASE}/api/dialogue/turn`;
    const body = guest
      ? {
          sceneKey: opts.sceneKey || sceneKey,
          messages: nextMessages,
          guestFields: useWorldStore.getState().guestFields,
        }
      : {
          sceneKey: opts.sceneKey || sceneKey,
          avatarId,
          messages: nextMessages,
          // Carried back every turn — it's what keeps the scene un-closeable while
          // a track renders. Drop it and the producer would end the session
          // mid-render, which is the whole thing we're avoiding.
          pending: opts.pending !== undefined ? opts.pending : pendingRef.current,
          // What this scene has already cut. `pending` clears when the render
          // lands, so on its own it stopped withholding start_track and the
          // session could quietly begin a second record.
          startedTrackId: opts.startedTrackId !== undefined ? opts.startedTrackId : startedRef.current,
        };
    const headers = { 'Content-Type': 'application/json' };
    if (!guest && AUTH_TOKEN) headers.Authorization = `Bearer ${AUTH_TOKEN}`;
    const res = await fetch(url, { method: 'POST', headers, body: JSON.stringify(body) });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(data.error || `dialogue ${res.status}`);
    // What the scene "recorded" for a guest exists nowhere else — bank it.
    if (guest && Array.isArray(data.recorded) && data.recorded.length) {
      const merged = {};
      data.recorded.forEach((r) => { if (r?.field && r?.value) merged[r.field] = r.value; });
      useWorldStore.getState().mergeGuestFields(merged);
    }
    if (Array.isArray(data.recorded) && data.recorded.length) setLastRecorded(data.recorded);
    return data;
  }, [sceneKey, avatarId]);

  // Quests are PULL-based: nothing needs telling when a field is written or a
  // track lands, we just ask afterwards whether the objectives now read true.
  const questCall = useCallback(async (path, body) => {
    if (!quest) return {};
    try {
      const res = await fetch(`${API_BASE}/api/quests/${path}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${AUTH_TOKEN}` },
        body: JSON.stringify({ avatarId, ...(body || {}) }),
      });
      return await res.json().catch(() => ({}));
    } catch { return {}; }
  }, [quest, avatarId]);

  // Opening a scene is keyed on the SCENE, not on "have we got messages yet":
  // guarding by transcript length meant a second scene opened after a first one
  // inherited the previous transcript and never fetched its own opening line.
  useEffect(() => {
    if (!sceneKey || (!avatarId && !IS_GUEST)) return undefined;
    if (openedRef.current === sceneKey) return undefined;
    openedRef.current = sceneKey;
    let dead = false;
    let landed = false;
    (async () => {
      setMessages([]); setClosed(false); setPendingBoth(null); setTrack(null);
      setNote(''); setChoices([]); setWorldNotes([]); setOpening(true); startedRef.current = null;
      // Starting a quest that is already COMPLETED flips it back to active on the
      // server (QuestService.start upserts status), which re-locks everything
      // chained behind it until the next sync. The phone's snapshot lists only
      // available/active quests, so a quest missing from a loaded list is
      // either done or not reachable yet — neither should be started.
      const known = useWorldStore.getState().phoneQuests || [];
      if (quest && (!known.length || known.some((q) => q.slug === quest))) questCall(`${quest}/start`);
      try {
        const data = await turn([], { sceneKey, pending: null });
        if (dead) return;
        landed = true;
        // Functional, not a bare array: a player can type before the opening
        // lands, and overwriting would delete what they just said. `audioUrl`
        // rides on the message — the phone plays her actual voice (cached
        // server-side, so the authored opening is instant).
        setMessages((m) => (m.length ? m : [{ role: 'assistant', content: data.reply, audioUrl: data.audioUrl || null, audioKey: data.audioKey || null }]));
        setChoices(data.choices || []);
        setVoiceAvatarId(data.voiceAvatarId || null);
      } catch (err) {
        if (!dead) { landed = true; setNote(humanError(err)); }
      } finally {
        if (!dead) setOpening(false);
      }
    })();
    return () => {
      dead = true;
      // StrictMode double-invokes effects in dev: the first run is torn down
      // before its fetch resolves, so it never sets the transcript or clears
      // `opening`. Releasing the latch lets the second run actually do the
      // work — without this the opening line never appears and the call sits
      // on "connecting…" forever while the player talks to nobody.
      if (!landed) openedRef.current = null;
    };
  }, [sceneKey, avatarId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Leaving the scene clears the latch, or re-entering the same room would show
  // an empty transcript with no opening line.
  useEffect(() => { if (!sceneKey) openedRef.current = null; }, [sceneKey]);

  // Watch the render. Without this the scene never resolves: `pending` stays
  // true forever, close_scene keeps being refused, and the producer stalls
  // indefinitely about a track that finished ten minutes ago.
  //
  // When it lands we clear `pending` — which re-arms close_scene — and drop a
  // line into the transcript so the player is TOLD, in the room, rather than
  // discovering it later in a menu.
  useEffect(() => {
    if (!track?.trackId || !pending?.running) return undefined;
    let dead = false;
    const tick = async () => {
      try {
        const res = await fetch(`${API_BASE}/api/tracks/${track.trackId}/generation-status`, {
          headers: { Authorization: `Bearer ${AUTH_TOKEN}` },
        });
        const data = await res.json().catch(() => ({}));
        if (dead) return;
        const done = data.status === 'complete' || data.trackStatus === 'ready';
        const failed = data.trackStatus === 'failed';
        if (done || failed) {
          setPendingBoth(null);
          // The result cards in the room read from the catalogue cache, and
          // nothing else knows the render finished — so the moment Book says
          // it's back, the card in front of you has it too.
          if (done) refreshMyRecords();
          setNote(failed ? 'The engineer will send it over later.' : '');
          setMessages((m) => [...m, {
            role: 'assistant',
            content: failed
              ? "Rig choked on that one. I'll send it over when it's fixed — you're not losing it."
              : `That's it back. “${data.title || track.title || 'Your track'}” — listen.`,
          }]);
        }
      } catch { /* transient — keep polling */ }
    };
    tick();
    pollRef.current = setInterval(tick, 6000);
    return () => { dead = true; clearInterval(pollRef.current); };
  }, [track?.trackId, pending?.running]);

  // SCENE AUDIO IS NOT WIRED, AND DELIBERATELY NOT VIA TTS.
  //
  // The obvious route — take the director's finished reply, POST it to
  // /api/chat/tts, wait for a whole clip to come back — is two serial round
  // trips before a single word is heard, on top of the turn that just generated
  // the text. Chat mode doesn't have that problem because the voice AGENT
  // streams as it forms; but a scene cannot simply borrow that agent, because
  // the agent brings its own LLM and a scene's words have to come from the
  // DIRECTOR (the agenda, record_field, the refusal to close while a track is
  // rendering). Point it at the wrong brain and Act 0 stops being Act 0.
  //
  // The real path is to make the director the agent's LLM, so
  // /api/dialogue/turn authors the words and the agent speaks them in the NPC's
  // voice with no clip to wait for. That is a change to the AGENT'S CONFIG, not
  // to this hook — so nothing here fakes it in the meantime.
  //
  // `voiceAvatarId` still comes down the wire: it names which avatar the NPC
  // speaks as, which is exactly what that wiring will need.

  // Queue a world note. Drained by the effect below only when the line is
  // free — never racing a player turn, never interrupting the opening fetch.
  // Capped: three landmarks backed up means the conversation is busy, and an
  // NPC reciting a backlog of scenery is worse than skipping some.
  const sendWorld = useCallback((text) => {
    const t = String(text || '').trim();
    if (!t) return;
    setWorldNotes((q) => [...q, t].slice(-3));
  }, []);

  // ── AUTHORED lines — the NPC speaks WITHOUT the model (Khari, 2026-08-02:
  // the ride's destination lore is cached, only real conversation goes live).
  // The line lands in the transcript as the NPC's own message, so the next
  // live turn's director sees he already said it — canon either way. Queued
  // and drained like world notes: never spliced into a turn in flight, where
  // the turn's setMessages would silently clobber it.
  const [authoredQueue, setAuthoredQueue] = useState([]);
  const sayAuthored = useCallback((line) => {
    const content = String(line?.content || '').trim();
    if (!content) return;
    setAuthoredQueue((q) => [...q, { content, audioUrl: line.audioUrl || null, followUp: line.followUp || null, image: line.image || null }]);
  }, []);
  // METERED, one line per beat (Khari, 2026-08-03: lines were "appearing too
  // quickly… speaking over itself" — cached delivery is instant, so the drain
  // has to make the space the old model latency used to provide). One
  // authored line lands per AUTHORED_GAP; a clip gets room to finish before
  // the next one starts, and the reader gets room to read.
  //
  // The followUp is a world note the authored line carries — "you've said the
  // lore, now get back to them" (his ride stalled until he typed "ok").
  // Queued when ITS line lands (so the follow-up turn's transcript contains
  // the line it follows), deduped by content, and the world-note drain below
  // HOLDS while authored lines are still queued — the question comes after
  // the lore cluster, never in the middle of it.
  const AUTHORED_GAP_MS = 7000;
  const lastAuthoredAtRef = useRef(0);
  useEffect(() => {
    // NOT gated on `closed` (Khari, 2026-08-03): Sassy can close the visit
    // while the look is still rendering — the reveal must still land in the
    // transcript. FollowUps queued after close just sit; the world-note
    // drain below refuses to send into a closed scene.
    if (!authoredQueue.length || sending || opening || !sceneKey) return undefined;
    const wait = Math.max(0, lastAuthoredAtRef.current + AUTHORED_GAP_MS - Date.now());
    const t = setTimeout(() => {
      lastAuthoredAtRef.current = Date.now();
      const line = authoredQueue[0];
      setAuthoredQueue((q) => q.slice(1));
      setMessages((m) => [...m, { role: 'assistant', content: line.content, audioUrl: line.audioUrl, image: line.image || null }]);
      if (line.followUp) {
        setWorldNotes((q) => (q.includes(line.followUp) ? q : [...q, line.followUp].slice(-3)));
      }
    }, wait);
    return () => clearTimeout(t);
  }, [authoredQueue, sending, opening, closed, sceneKey]);

  // ── LATE-ARRIVING VOICE (Khari, 2026-08-03: replies came "extremely slow")
  // The server no longer waits on synthesis: a turn whose clip wasn't cached
  // returns `audioKey` and renders in the background. Poll the cache-only
  // endpoint and patch the URL onto the message when it lands — the playing
  // surfaces watch messages, so the line starts speaking the moment it's
  // ready. Budget ~25s: a real synth pays the JIT voice add→TTS→delete cycle
  // plus the render (Khari's log showed clips landing AFTER the old ~8s
  // window — "not hearing any of these"). A late clip is safe: playback only
  // fires while its message is still the newest, so nothing barges in.
  useEffect(() => {
    if (!sceneKey) return undefined;
    const idx = messages.length - 1;
    const m = messages[idx];
    if (!m || m.role !== 'assistant' || !m.audioKey || m.audioUrl) return undefined;
    const key = m.audioKey;
    let dead = false;
    let tries = 0;
    console.info(`[voice] clip pending (${key.slice(0, 8)}…) — polling`);
    const poll = async () => {
      if (dead) return;
      tries += 1;
      try {
        const r = await fetch(`${API_BASE}/api/dialogue/line-audio/${key}`);
        const d = await r.json().catch(() => ({}));
        if (dead) return;
        if (d?.url) {
          console.info(`[voice] clip landed (${key.slice(0, 8)}…) after ${tries} poll(s)`);
          setMessages((cur) => cur.map((x, i) => (i === idx && x.audioKey === key
            ? { ...x, audioUrl: d.url, audioKey: null } : x)));
          return;
        }
      } catch (err) {
        if (tries === 1) console.warn('[voice] poll fetch failed (will retry):', err?.message);
      }
      if (tries < 30) setTimeout(poll, 800);
      else console.warn(`[voice] clip never landed (${key.slice(0, 8)}…) — staying text`);
    };
    const t = setTimeout(poll, 700);   // synth needs a beat — don't poll at 0ms
    return () => { dead = true; clearTimeout(t); };
  }, [messages, sceneKey]);

  useEffect(() => {
    if (!worldNotes.length || sending || opening || closed || !sceneKey) return;
    // Authored lines still queued = the NPC is mid-lore. The live turn (and
    // any followUp nudge) waits its turn behind them.
    if (authoredQueue.length) return;
    const batch = worldNotes;
    setWorldNotes([]);
    const line = { role: 'user', content: batch.join(' '), world: true };
    const next = [...messages, line];
    setMessages(next); setSending(true); setChoices([]);
    (async () => {
      try {
        const data = await turn(next);
        setMessages([...next, { role: 'assistant', content: data.reply, audioUrl: data.audioUrl || null, audioKey: data.audioKey || null }]);
        setChoices(data.choices || []);
        setVoiceAvatarId(data.voiceAvatarId || null);
        setWallet(data.wallet || null);
        if (data.pending?.running) setPendingBoth(data.pending);
        if (data.closed) {
          setClosed(true);
          if (quest) questCall(`${quest}/sync`);
          if (IS_GUEST) {
            const st = useWorldStore.getState();
            if (sceneKey === 'act0_lyra_call') st.setGuestProgress({ arrivalDone: true });
            if (sceneKey === 'act2_ride_spor1') st.setGuestProgress({ rideDone: true });
          }
        }
      } catch {
        // A lost note is scenery, not progress — drop it rather than surfacing
        // an error about a sentence the player never typed.
        setMessages(messages);
      } finally {
        setSending(false);
      }
    })();
  }, [worldNotes, authoredQueue, sending, opening, closed, sceneKey]); // eslint-disable-line react-hooks/exhaustive-deps

  // `display` exists because a TAPPED choice submits a canonical spec key —
  // that is what makes the server able to record it without asking a model —
  // but the key is not English. Khari, 2026-08-06: his heritage tap appeared in
  // the transcript as `african_diaspora`, in his own speech bubble. The server
  // still receives the key; only the bubble shows the label.
  const send = useCallback(async (text, display = null) => {
    const body = String(text ?? input).trim();
    if (!body || sending || closed) return;
    const next = [...messages, { role: 'user', content: body, ...(display && display !== body ? { display } : {}) }];
    setMessages(next); setInput(''); setSending(true); setNote(''); setChoices([]);
    try {
      const data = await turn(next);
      setMessages([...next, { role: 'assistant', content: data.reply, audioUrl: data.audioUrl || null, audioKey: data.audioKey || null }]);
      setChoices(data.choices || []);
      setVoiceAvatarId(data.voiceAvatarId || null);
      setWallet(data.wallet || null);
      if (data.pending?.running) setPendingBoth(data.pending);
      if (data.trackStarted) {
        startedRef.current = data.trackStarted.trackId;
        setTrack({ trackId: data.trackStarted.trackId, title: data.trackStarted.title });
        setNote(`“${data.trackStarted.title || 'Your track'}” is rendering…`);
      }
      // The premiere (§4.6): the producer released it on the player's yes. The
      // WORLD delivers the first play — hand the payload to the store the
      // moment it exists, so walking out is all it takes.
      if (data.premiere) {
        useWorldStore.getState().setPremiere({ ...data.premiere, avatarId });
      }
      // THE HANDOFF (§4.7): autonomy just changed hands. The store carries it
      // so the world can stage the walk — after the call ends, not during.
      if (data.granted) {
        useWorldStore.getState().setHandoff({ avatarId });
      }
      if (data.recorded?.length && quest) questCall(`${quest}/sync`);
      if (data.closed) {
        setClosed(true);
        if (quest) {
          const synced = await questCall(`${quest}/sync`);
          if (synced?.justCompleted) setNote('Quest complete.');
        }
        // Guest progress has no quest rows — the client is the ledger. These
        // marks are what advance the guest intro: call done → the Lotus shows;
        // ride done → the salon (and its signup) is next.
        if (IS_GUEST) {
          const st = useWorldStore.getState();
          if (sceneKey === 'act0_lyra_call') st.setGuestProgress({ arrivalDone: true });
          if (sceneKey === 'act2_ride_spor1') st.setGuestProgress({ rideDone: true });
        }
      }
    } catch (err) {
      setNote(humanError(err));
    } finally {
      setSending(false);
    }
  }, [input, sending, closed, messages, turn, questCall, quest]);

  return {
    messages, input, setInput, send, sendWorld, sayAuthored, sending, opening, choices,
    closed, note, setNote, pending, track, questCall,
    voiceAvatarId, wallet, setWallet, lastRecorded,
  };
}

export default useDialogueScene;
